import {getOverlayBounds} from "@churchofjesuschrist/maps-google-overlays";

export default function DataService($rootScope, $q, DataConst, MapService, NotificationService, OrgService, utils) {
	"ngInject";

	const store = {
		missionId: null,
		orgs: null,
		map: null,
		mapOpts: {
			selectedOrg: null,
			staticOrgs: null,
			automaticOrgs: null,
			onLabelClick: null,
			labelsClean: null,
			force: null
		}
	};

	const defers = {
		orgs: $q.defer(),
		missionExtened: null,
		map: $q.defer()
	};

	const loadService = (fn, prop, loadProps) => (
		(org) => {
			if (org && !org[prop]) {
				let layer = MapService.getLayer(org.type);
				return MapService[fn](org.id, layer).then((results) => {
					loadProps(org, results);
					return org;
				});
			} else {
				let d = $q.defer();
				setTimeout(() => {
					d.resolve(org);
				});
				return d.promise;
			}
		}
	);

	const getOrg = (id, type) => {
		let org = null;
		store.orgs.some((current) => {
			if (current.type === type && (id ? current.id === id : type === "MISSION")) {
				org = current;
				return true;
			}
		});
		return org;
	};

	const getOrgByIsUnitType = (id, isUnitType) => {
		let org = null;
		store.orgs.some((current) => {
			if (current.id === id && utils.isUnit(current) === isUnitType) {
				org = current;
				return true;
			}
		});
		return org;
	};

	const getOrgsByIsUnitType = (ids, isUnitType) => (
		ids.map((id) => (getOrgByIsUnitType(id, isUnitType)))
	);


	const DataService = {
		loadMap: (map) => {
			store.map = map;
			defers.map.resolve(map);
			return DataService.getMap();
		},
		getMap: () => (
			defers.map.promise
		),
		instantMap: () => (
			store.map
		),

		getMapOpts: () => (
			store.mapOpts
		),
		setMapOpts: (opts = {}) => {
			let prevOpts = store.mapOpts;
			store.mapOpts = opts;
			$rootScope.$broadcast("mapOptsChanged", store.mapOpts, prevOpts);
		},
		updateMapOpts: (opts) => {
			let prevOpts = store.mapOpts;
			DataService.setMapOpts(angular.extend({}, prevOpts, opts));
		},

		loadMission: (missionId) => {
			if (store.missionId !== missionId) {
				store.missionId = missionId;
				store.orgs = null;

				const orgs = [];
				let missionError = false;

				let orgsPromise = OrgService.getMissionOrgs(missionId).then((results) => {
					if (results) { //check for undefined as it will error
						orgs.push(...results);
					}
				}, (error) => {
					if (error.status === 403) {
						NotificationService.error("pros-maps.error.permission");
						missionError = true;
					}
				});

				let boundsPromise = MapService.getBounds(missionId);

				let customPromise = MapService.getCustoms(missionId).then((results) => {
					orgs.push(...results);
				});

				let facilitiesPromise = MapService.getFacilities(missionId).then((results) => {
					orgs.push(...results);
				});

				$q.all([orgsPromise, boundsPromise, customPromise, DataService.getMap(), facilitiesPromise]).then((results) => {
					if (!missionError) {
						store.orgs = orgs.sort(utils.sortOrgs);

						let mission = store.orgs[0];
						mission.bounds = results[1];

						defers.orgs.resolve(store.orgs);
					}
				});
			}
			return DataService.getMission();
		},
		loadMissionBoundary: (callback) => (
			DataService.getMission().then((mission) => {
				let defer = $q.defer(),
					finished = false;

				DataService.loadBoundary(mission).then(() => {
					if (callback && !finished) {
						callback(mission);
					}
					defer.resolve(mission);
				});

				DataService.loadMissionExtendedBoundary().then(() => {
					finished = true;
					if (callback) {
						callback(mission);
					}
					defer.resolve(mission);
				});

				return defer.promise;
			})
		),
		loadMissionExtendedBoundary: () => {
			if (!defers.missionExtened) {
				defers.missionExtened = $q.defer();
				DataService.getOrgsByTypes(["MISSION", "ZONE"]).then((orgs) => {
					DataService.mergeOrgs(orgs, true).then((boundary) => {
						let mission = orgs[0];
						mission.extended = {
							boundary,
							bounds: getOverlayBounds(boundary)
						};
						defers.missionExtened.resolve(mission);
					}, (ex) => {
						defers.missionExtened.reject(ex);
					});
				});
			}
			return defers.missionExtened.promise;
		},
		getMissionOrgs: () => (
			defers.orgs.promise
		),

		getMission: () => (
			DataService.getOrg(null, "MISSION")
		),
		getZone: (zoneId) => (
			DataService.getOrg(zoneId, "ZONE")
		),
		getDistrict: (districtId) => (
			DataService.getOrg(districtId, "DISTRICT")
		),
		getArea: (areaId) => (
			DataService.getOrg(areaId, "AREA")
		),
		getCustom: (customId) => (
			DataService.getOrg(customId, "CUSTOM")
		),
		getAllCustoms: () => (
			DataService.getOrgsByTypes(["CUSTOM"])
		),

		loadDetails: loadService("getDetails", "layer", (org, details) => {
			org.layer = details.type;
			org.type = MapService.getType(org.layer);
			org.bounds = details.extent;
			org.position = details.coordinates;
		}),
		loadBoundary: loadService("getBoundary", "boundary", (org, boundary) => {
			org.boundary = boundary;
			org.bounds = getOverlayBounds(boundary);
		}),
		loadBounds: loadService("getBounds", "bounds", (org, bounds) => {
			org.bounds = bounds;
		}),
		loadCentroids: loadService("getCentroids", "centroids", (org, centroids) => {
			org.centroids = centroids;
		}),
		mergeOrgs: (orgs, ignoreMissingBoundaries) => {
			let promises = orgs.filter(DataService.hasBoundary).map(DataService.loadBoundary),
				allFn = ignoreMissingBoundaries ? utils.allPromisesCompleted : $q.all,
				catchFn = ignoreMissingBoundaries ? angular.noop : undefined;

			return allFn(promises).then(() => {
				let boundaries = orgs.filter(utils.getOrgBoundary).map(utils.getOrgBoundary);
				return MapService.merge(boundaries);
			}, catchFn);
		},

		updateArea: (area) => (
			DataService.getArea(area.id).then((current) => (
				// update org data
				angular.extend(current, area)
			))
		),
		updateCustom: (custom) => (
			DataService.getCustom(custom.id).then((current) => {
				if (current) {
					angular.extend(current, custom);
				} else {
					store.orgs.push(custom);
				}
				store.orgs.sort(utils.sortOrgs);
			})
		),
		removeCustom: (custom) => (
			DataService.getCustom(custom.id).then((current) => {
				utils.removeItem(current, store.orgs);
			})
		),

		getOrg: (id, type) => (
			DataService.getMissionOrgs().then(() => {
				let org = getOrg(id, type);
				return DataService.hasBoundary(org) ? DataService.loadBounds(org) : org;
			})
		),
		getOrgParent: (id, type) => (
			DataService.getMissionOrgs().then((orgs) => {
				let parent = null;
				if (type === DataConst.CUSTOM_TYPE) {
					parent = orgs[0];
				} else {
					let unitType = utils.isUnit(type);
					orgs.some((org) => {
						if (org.assigned && org.assigned.some((childId) => (childId === id)) && utils.isAssignedUnits(org.type) === unitType) {
							parent = org;
							return true;
						}
					});
				}
				return parent;
			})
		),
		getOrgParents: (id, type) => {
			const getOrgParents = (id, type, parents) => (
				DataService.getOrgParent(id, type).then((parent) => {
					if (parent) {
						parents.push(parent);
						return getOrgParents(parent.id, parent.type, parents);
					} else {
						return parents;
					}
				})
			);

			return getOrgParents(id, type, []);
		},
		getOrgsByTypes: (types = []) => {
			let promises = [];
			return DataService.getMissionOrgs().then((orgs) => {
				let specificUnitTypes = types.filter((type) => (utils.isUnit(type) && DataConst.GENERIC_UNIT_ORG_TYPES.indexOf(type) === -1));
				if (specificUnitTypes.length) {
					promises.push(DataService.loadUnitDetails());
				}
				let otfTypes = types.filter((type) => (DataConst.OTF_ORG_TYPES.indexOf(type) !== -1));
				if (otfTypes.length) {
					for (let type of otfTypes) {
						//load meeting houses and possibly others on the fly since there could be a lot
						// Should only run when one of the non automatic types are selected
						if (!MapService.getOnTheFlyAssociatedAlreadyLoaded(type)) {
							return MapService.getOnTheFlyAssociated(store.missionId, type).then((results) => {
								store.orgs.push(...results);
								store.orgs = store.orgs.sort(utils.sortOrgs);
								return store.orgs.filter((org) => (types.indexOf(org.type) !== -1))
							});
						} else {
							let def = $q.defer();
							def.resolve(store.orgs.filter((org) => (types.indexOf(org.type) !== -1)));
							promises.push(def.promise);
						}
					}
				}
				return $q.all(promises).then(() => (
					orgs.filter((org) => (types.indexOf(org.type) !== -1))
				));
			})
		},

		loadUnitDetails: (unitIds) => (
			DataService.getOrgsByTypes(DataConst.GENERIC_UNIT_ORG_TYPES).then((unitsNotLoaded) => {
				let unitsToLoad = unitsNotLoaded.filter((unit) => (!unitIds || unitIds.indexOf(unit.id) !== -1));
				return $q.all(unitsToLoad.map(DataService.loadDetails)).then(() => (
					unitIds ? getOrgsByIsUnitType(unitIds, true) : null
				));
			})
		),

		getOverlappingAreas: (area) => (
			DataService.getOrgsByTypes(["AREA"]).then((allAreas) => {
				let areas = [],
					areaId = area.id,
					unitIds = DataService.getAssignedBoundaryUnits(area).map((unit) => (unit.id));

				allAreas.forEach((otherArea) => {
					if (otherArea.referrals && otherArea.id !== areaId) {
						let otherUnitIds = DataService.getAssignedBoundaryUnits(otherArea).map((unit) => (unit.id)),
							matchingUnits = otherUnitIds.filter((unitId) => (unitIds.indexOf(unitId) !== -1));
						if (matchingUnits.length) {
							areas.push(otherArea);
						}
					}
				});

				areas.sort(utils.sortOrgs);

				return areas;
			})
		),
		getAssignedOrgs: (org) => {
			let assigned = null;
			if (org.assigned) {
				let isChildUnits = utils.isAssignedUnits(org.type);
				assigned = org.assigned.map((id) => (
					getOrgByIsUnitType(id, isChildUnits)
				));
			}
			return assigned;
		},
		getAssignedBoundaryOrgs: (org) => (
			DataService.getAssignedOrgs(org).filter(DataService.hasBoundary)
		),
		getAssignedBoundaryUnits: (area) => {
			let units = [];
			if (area.assigned) {
				DataService.getAssignedBoundaryOrgs(area)
					.forEach((unit) => {
						units.push(unit);
						if (unit.assigned) {
							units.push(...DataService.getAssignedBoundaryOrgs(unit));
						}
					});
				units = utils.uniqueOrgs(units);
			}
			return units;
		},
		hasAssignedBoundaryUnits: (org) => {
			let hasUnits = false;
			if (org) {
				if (DataConst.BOUNDARY_UNIT_ORG_TYPES.indexOf(org.type) !== -1) {
					hasUnits = true;
				} else if (org.assigned) {
					hasUnits = DataService.getAssignedOrgs(org).some(DataService.hasAssignedBoundaryUnits);
				}
			}
			return hasUnits;
		},
		getChildLayerUnits: (parentUnit) => {
			let sublayer = MapService.getSubLayer(parentUnit.layer);
			return (DataService.getAssignedOrgs(parentUnit) || []).filter((unit) => (
				sublayer === MapService.getSubLayer(unit.layer)
			));
		},
		hasBoundary: (org) => (
			org && (org.boundary || org.type === DataConst.CUSTOM_TYPE || DataService.hasAssignedBoundaryUnits(org))
		),
		hasFacility: (org) => (
			org && (DataService.isFacility(org) || (org.assigned && DataService.getAssignedOrgs(org).some(DataService.isFacility)))
		),
		isFacility: (org) => (
			org && (DataConst.FACILITY_UNIT_ORG_TYPES.indexOf(org.type) !== -1)
		),

		getCompiledBoundaryStatus: (org) => {
			let status = null;
			if (org && DataConst.MISSION_ORG_TYPES.indexOf(org.type) !== -1) {
				status = org.boundaryStatus;
				if (!status && org.assigned && org.type !== "AREA") {
					DataService.getAssignedOrgs(org).forEach((child) => {
						let childStatus = DataService.getCompiledBoundaryStatus(child);
						if (childStatus && (!status || utils.isBoundaryStatusHigher(childStatus, status))) {
							status = childStatus;
						}
					});
				}
			}
			return status;
		},
		getCompiledReferralsStatus: (org) => {
			let status = null;
			if (org && DataConst.MISSION_ORG_TYPES.indexOf(org.type) !== -1) {
				status = false;
				if (org.type === "AREA") {
					status = org.referrals;
				} else if (org.assigned) {
					DataService.getAssignedOrgs(org).forEach((child) => {
						let childStatus = DataService.getCompiledReferralsStatus(child);
						if (childStatus) {
							status = childStatus;
						}
					});
				}
			}
			return status;
		},

		isStatusFlagged: (org) => {
			if (angular.isArray(org)) {
				return org.filter(DataService.isStatusFlagged);
			} else {
				let status = DataService.getCompiledBoundaryStatus(org);
				return status === "FLAGGED" || status === "RESET";
			}
		},
		isNonReferrals: (org) => {
			if (angular.isArray(org)) {
				return org.filter(DataService.isNonReferrals);
			} else {
				let status = DataService.getCompiledReferralsStatus(org);
				return status !== null && !status;
			}
		},
		isBoundaryPending: (org) => {
			if (angular.isArray(org)) {
				return org.filter(DataService.isBoundaryPending);
			} else {
				return org && org.syncStatus && org.syncStatus !== "SYNCED";
			}
		}
	};
	return DataService;
}
